"use client";

import { useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";
import { useThemeConfig } from "@/hooks/use-theme-config";

interface SkeletonProps extends React.ComponentProps<"div"> {
  /** Show terminal-style "LOADING..." text overlay */
  showLoadingText?: boolean;
}

function Skeleton({ className, showLoadingText = false, ...props }: SkeletonProps) {
  const { isTerminal, hasGlow } = useThemeConfig();

  // Hydration-safe pattern: only show terminal styling after mount
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  // Use safe values during SSR
  const showTerminal = mounted && isTerminal;
  const showGlow = mounted && hasGlow;

  return (
    <div
      data-slot="skeleton"
      className={cn(
        "rounded-md relative overflow-hidden",
        // Base styling - conditional on terminal mode
        showTerminal
          ? "bg-black border border-emerald-500/30"
          : "bg-accent",
        // Shimmer animation
        "animate-pulse",
        className
      )}
      {...props}
    >
      {/* Terminal mode: emerald gradient shimmer overlay */}
      {showTerminal && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500/10 to-transparent animate-shimmer"
          style={{
            backgroundSize: '200% 100%',
          }}
        />
      )}

      {/* Terminal mode: static glow effect */}
      {showGlow && (
        <div className="absolute inset-0 shadow-[inset_0_0_8px_rgba(16,185,129,0.15)]" />
      )}

      {/* Terminal mode: "LOADING..." text overlay */}
      {showTerminal && showLoadingText && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-mono text-[10px] text-emerald-500/60 uppercase tracking-widest">
            LOADING...
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Terminal-styled skeleton for larger content areas
 * Shows "LOADING..." text and enhanced glow effects
 */
function TerminalSkeleton({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <Skeleton
      className={cn("min-h-[100px]", className)}
      showLoadingText={true}
      {...props}
    />
  );
}

export { Skeleton, TerminalSkeleton };
